import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { buttonClasses } from '@/design-system/Button'
import { Container } from '@/design-system/primitives'
import { useAwards, usePublicStandings } from '@/features/competition/api'
import { AwardsSection, StandingsSection } from '@/features/competition/components'
import { usePublicVoteCategories } from '@/features/voting/api'
import { VoteResultsSection } from '@/features/voting/components'

import { RubricPage } from '../components/RubricPage'

/**
 * Votes et résultats, tour par tour — la page du soir J.
 *
 * Elle doit rester juste pendant qu'on la regarde : les tours s'ouvrent et se
 * ferment au micro, les podiums tombent les uns après les autres. Un cache
 * figé afficherait un vainqueur périmé devant la salle, on rafraîchit donc en
 * continu tant que l'onglet est visible.
 */
export function ResultsPage() {
  const { t } = useTranslation()

  return (
    <RubricPage title={t('rubrics.results.title')} intro={t('rubrics.results.body')}>
      {(event) => <ResultsBody slug={event.slug} />}
    </RubricPage>
  )
}

function ResultsBody({ slug }: { slug: string }) {
  const { t } = useTranslation()

  // 15 s : assez court pour suivre une annonce, assez long pour que trois
  // cents téléphones sur le même wifi ne noient pas l'API.
  const voteQuery = usePublicVoteCategories(slug, { refetchInterval: 15_000 })
  const awardsQuery = useAwards(slug)
  // Le titre se joue sur le cumul (public + jury) : c'est lui qu'on montre en
  // premier. Avant la proclamation, chaque titre dit seulement « à venir » ;
  // proclamé, il donne un nom ; les chiffres — par tour et cumulés — n'arrivent
  // qu'avec la publication des scores. Le serveur n'envoie rien de plus tôt.
  const standingsQuery = usePublicStandings(slug, { refetchInterval: 15_000 })

  const categories = voteQuery.data ?? []
  // Une catégorie ouverte au vote : on renvoie vers le bulletin, qui vérifie
  // le billet et le quota. Ce lien n'ouvre aucun droit par lui-même.
  // Le départage ouvert n'en est pas un pour la salle : seuls cinq jurés y
  // votent, et « Voter maintenant » enverrait trois cents personnes vers un
  // bulletin qui leur répondrait « réservé au jury ».
  const openForVoting = categories.some(
    (category) => category.status === 'open' && !category.is_tiebreak,
  )

  return (
    <>
      {openForVoting ? (
        <Container className="pt-10">
          <div className="flex flex-col gap-4 rounded-lg border border-champagne-400/40 bg-bg-elevated p-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-body-lg text-content-primary">{t('results.voteOpen')}</p>
            <Link to="/vote" className={buttonClasses({ className: 'shrink-0' })}>
              {t('results.voteNow')}
            </Link>
          </div>
        </Container>
      ) : null}

      <StandingsSection contests={standingsQuery.data ?? []} />
      {/* Les votes libres seulement : les tours du concours sont déjà au-dessus,
          dans le cumul — et seulement une fois ses scores publiés. */}
      <VoteResultsSection categories={categories} />
      <AwardsSection awards={awardsQuery.data ?? []} />
    </>
  )
}
